/**
 * The public page frame: header with the wordmark, region flags and nav; the footer with the one
 * sentence about what we are not. Contextual sidebar help belongs to the authenticated platform;
 * public pages use their visible copy and the dedicated `/help` route without platform help chrome.
 */
import { Fragment, useEffect, useState, type ReactNode as RN } from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router";
import { webRegionsConfig } from "@oc/config";
import { NOT_ADVICE, NOT_YOUR_AGENT_FOOTER } from "@oc/copy";
import type { Surface } from "@oc/help";
import { h, SiteHeader, SiteFooter, RegionFlags, EntityLine, type ReactNode } from "@oc/ui";
import { isLive, regionFor, useRegion, type Region } from "./region.ts";
import { FOOTER, NAV, NOT_FOUND } from "./copy.ts";
import { api, type SiteIdentity } from "./api.ts";

export function RegionLayout(): ReactNode {
  const { region } = useParams();
  if (!isLive(region)) return h(NotFound);
  return h(Outlet);
}

/** One public page: header, body and footer. `screen` scopes the editorial presentation. */
export function Page({ screen, children, nav }: { surface?: Surface; screen: string; children: RN; nav?: "site" | "review" }): ReactNode {
  const r = useRegion();
  const loc = useLocation();
  const [menu, setMenu] = useState(false);
  const flags = h(RegionFlags, {
    live: webRegionsConfig.live.map((code) => ({ code, label: regionFor(code).web.name, href: `/${code}${loc.pathname.replace(/^\/[a-z]{2}/, "")}`, current: code === r.code })),
    soon: webRegionsConfig.soon.map((s) => ({ code: s.market, label: s.label })),
  });
  const links = nav === "review"
    ? [{ label: NAV.what, href: `${r.href("/review")}#what` }, { label: NAV.how_it_works, href: `${r.href("/review")}#how` }, { label: NAV.price, href: `${r.href("/review")}#price` }, { label: NAV.research, href: r.href("/") }, { label: NAV.start, href: r.href("/start"), strong: true }]
    : [{ label: NAV.today, href: `${r.href("/")}#today` }, { label: NAV.active, href: r.href("/active-offers") }, { label: NAV.how, href: `${r.href("/")}#discovery` }, { label: NAV.review, href: r.href("/review") }, { label: NAV.start, href: r.href("/start"), strong: true }];
  return h(Fragment, null,
    h(SiteHeader, { homeHref: r.href("/"), flags, nav: links, menuOpen: menu, onToggleMenu: () => setMenu((m) => !m) }),
    h("main", { className: "oc-public-main", "data-screen": screen }, children),
    h(Footer, { r }),
  );
}

export function Footer({ r }: { r: Region }): ReactNode {
  const [identity, setIdentity] = useState<SiteIdentity | null>(null);
  useEffect(() => {
    if (r.code !== "au") return;
    let current = true;
    api.siteIdentity(r.code)
      .then((next) => { if (current) setIdentity(next); })
      .catch(() => { /* The configured regional brand remains a valid non-blocking fallback. */ });
    return () => { current = false; };
  }, [r.code]);
  return h(SiteFooter, {
    about: h("span", null, `${FOOTER.about_1} `, h("b", { style: { fontWeight: 600 } }, NOT_YOUR_AGENT_FOOTER), ` ${FOOTER.about_2_prefix} ${r.advice.line}. ${FOOTER.about_3}`),
    groups: [
      { group: FOOTER.product, links: [{ label: FOOTER.free, href: r.href("/start") }, { label: FOOTER.price, href: `${r.href("/review")}#price` }, { label: FOOTER.sample, href: `${r.href("/details")}#samples` }, { label: FOOTER.note, href: r.href("/research") }, { label: NAV.active, href: r.href("/active-offers") }] },
      { group: FOOTER.detail, links: [{ label: FOOTER.in_review, href: `${r.href("/details")}#review` }, { label: FOOTER.marks, href: `${r.href("/details")}#marks` }, { label: r.web.broker_nav, href: `${r.href("/details")}#brokers` }] },
      { group: FOOTER.legal, links: [{ label: FOOTER.privacy, href: `${r.href("/legal")}#privacy` }, { label: FOOTER.terms, href: `${r.href("/legal")}#terms` }, { label: r.web.tax_label, href: `${r.href("/legal")}#refund` }] },
    ],
    entityLine: h(EntityLine, { text: entityLine(r, identity) }),
    adviceLine: r.advice.line,
  });
}

export function entityLine(r: Region, identity: SiteIdentity | null): string {
  if (!identity?.configured || !identity.legal_name) return r.web.entity_line;
  return identity.abn ? `${identity.legal_name} · ABN ${identity.abn}` : identity.legal_name;
}

/** A same-app link; the router handles it, the markup is an anchor. */
export function A({ href, children, className, style }: { href: string; children: RN; className?: string; style?: Record<string, string> }): ReactNode {
  return h(Link, { to: href, className, style }, children);
}

export function NotFound(): ReactNode {
  const nav = useNavigate();
  return h("div", { className: "oc-wrap", style: { padding: "64px 24px" } },
    h("h1", { className: "oc-h1" }, NOT_FOUND.h),
    h("p", { className: "oc-body-lg oc-secondary", style: { marginTop: "14px" } }, NOT_FOUND.body),
    h("div", { style: { marginTop: "20px" } }, h("button", { type: "button", className: "btn-2", onClick: () => nav("/au") }, NOT_FOUND.home)));
}

export const mailto = (addr: string, subject: string) => `mailto:${addr}?subject=${encodeURIComponent(subject)}`;
